import { ShopSystemConfiguration } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'
import {
  addProductToCart,
  addSystemConfigurationToCart,
  deleteProductFromCart,
  fetchAddresses,
  fetchCheckoutProducts,
  fetchCountries,
  fetchPaymentMethod,
  storeShippingAddress,
  updateQuantityProductToCart,
  updateDesiredShippingDate,
  storeTemporaryShippingAddress,
  requestOrder,
  addRetrofitBatteryToCart,
  addPromotionCodeToCart,
  updateCartConfigurator,
  fetchShippingMethods,
  updateShippingMethod,
} from '../api/shop-cart.api'
import { AddressesData, PaymentMethodResponseModel, ShippingWithAvis, ShopCheckoutSideBarResponse } from '../models'
import { useShopFetchWithResult } from './use-shop-fetch'
import { useState } from 'react'
import { CountriesModelResponse, ShippingAddressForm } from '../models/new-shipping-address-modal.model'
import { CreateOrderResponseModel } from '../models/create-order.model'
import { useDispatch } from 'react-redux'
import { updateShopBasketQuantity } from 'src/State/Shop/ShopAction'
import { basketQuantityCounter, shopNotificationKeys } from '../utils'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ShopAddToCartProps } from '../models/shop-checkout-side-bar.model'
import { AuthShopFetchError } from '../types'
import useShopAdmin from './use-shop-admin'
import { ShippingMethodResponseModel } from '../models/shipping-method-modal.model'
import { QntmSystemConfiguration } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationModel'

/*
 * This hook holds all the endpoints that we use on the Cart (Basket) , like Adding / Removing or Changing Product or System Configurator
 * also the endpoints like Shipping Method or Payment Method that we use on the Cart.
 */

interface UseShopCartHook {
  getCheckoutProducts: () => Promise<ShopCheckoutSideBarResponse | null>
  changeProductCartQuantity: (productId: string, quantity: number) => Promise<ShopCheckoutSideBarResponse | null>
  deleteProductCart: (productId: string | string[]) => Promise<ShopCheckoutSideBarResponse | null>
  addProductCart: (productId: string | ShopAddToCartProps | ShopAddToCartProps[]) => Promise<ShopCheckoutSideBarResponse | null>
  addSystemConfigurationCart: (configurator: ShopSystemConfiguration | QntmSystemConfiguration) => Promise<ShopCheckoutSideBarResponse | null>
  updateSystemConfigurationCart: (
    cartItemId: string,
    configurator: ShopSystemConfiguration | QntmSystemConfiguration
  ) => Promise<ShopCheckoutSideBarResponse | null>
  loadPaymentMethod: () => Promise<PaymentMethodResponseModel | null>
  getAddresses: () => Promise<AddressesData | null>
  saveShippingAddress: (addressInformation: ShippingAddressForm) => Promise<ShippingAddressForm | null>
  getShippingMethods: () => Promise<ShippingMethodResponseModel | null>
  setShippingMethod: (shippingMethodId: string) => Promise<PaymentMethodResponseModel | null>
  getCountries: () => Promise<CountriesModelResponse | null>
  setDesiredShippingDate: (day: number, month: number, year: number) => Promise<ShopCheckoutSideBarResponse | null>
  saveTemporaryShippingAddress: (addressInformation: ShippingAddressForm) => Promise<ShippingAddressForm | null>
  addRetrofitBatteryCart: (productId: string, quantity: number, serialNumber: string) => Promise<ShopCheckoutSideBarResponse | null>
  addPromotionCodeCart: (code: string) => Promise<ShopCheckoutSideBarResponse | null>
  requestCartOrder: (
    billingAddressName: string,
    shippingAddressName?: string | null,
    customerReferenceNumber?: string | null,
    customerComment?: string | null,
    avisInfo?: ShippingWithAvis | null
  ) => Promise<CreateOrderResponseModel | null>
  checkHasShopCartErrors: (cartData: ShopCheckoutSideBarResponse | null) => boolean
  isLoading: boolean
}

const useShopCart = (): UseShopCartHook => {
  const shopFetch = useShopFetchWithResult()
  const { checkIsShopUserAdmin } = useShopAdmin()
  const { t } = useCustomTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const isShopAdmin = checkIsShopUserAdmin()

  const checkHasShopCartErrors = (cartData: ShopCheckoutSideBarResponse | null): boolean => {
    if (cartData?.errors && Object.keys(cartData.errors).length > 0) {
      Object.keys(cartData.errors).forEach(errorKey => {
        const errorMessage = cartData.errors[errorKey]
        if (shopNotificationKeys.some(pattern => errorKey.includes(pattern))) {
          enqueueSuccessStack(t(`shop.error.${errorMessage.messageKey}`))
        } else {
          enqueueErrorStack(t(`shop.error.${errorMessage.messageKey}`))
        }
      })
      return true
    }
    return false
  }

  const getCheckoutProducts = async (): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      const response = await fetchCheckoutProducts(shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      return response.value
    } catch (error) {
      return null
    }
  }

  const changeProductCartQuantity = async (productId: string, quantity: number): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await updateQuantityProductToCart(productId, quantity, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      const hasCartErrors = checkHasShopCartErrors(response.value)
      if (hasCartErrors) return response.value
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateSystemConfigurationCart = async (
    cartItemId: string,
    configurator: ShopSystemConfiguration | QntmSystemConfiguration
  ): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const systemConfigurationWithUpdatedMonth = {
        ...configurator,
        desiredShippingDate: {
          ...configurator.desiredShippingDate,
          month: configurator.desiredShippingDate.month !== null ? configurator.desiredShippingDate.month + 1 : null,
        },
      }
      const response = await updateCartConfigurator(cartItemId, systemConfigurationWithUpdatedMonth, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteProductCart = async (productId: string | string[]): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await deleteProductFromCart(productId, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      checkHasShopCartErrors(response.value)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const addProductCart = async (product: string | ShopAddToCartProps | ShopAddToCartProps[]): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await addProductToCart(product, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      const hasCartErrors = checkHasShopCartErrors(response.value)
      if (hasCartErrors) return null
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const addSystemConfigurationCart = async (
    configurator: ShopSystemConfiguration | QntmSystemConfiguration
  ): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const systemConfigurationWithUpdatedMonth = {
        ...configurator,
        desiredShippingDate: {
          ...configurator.desiredShippingDate,
          month: configurator.desiredShippingDate.month !== null ? configurator.desiredShippingDate.month + 1 : null,
        },
      }
      const response = await addSystemConfigurationToCart(systemConfigurationWithUpdatedMonth, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const loadPaymentMethod = async (): Promise<PaymentMethodResponseModel | null> => {
    try {
      setIsLoading(true)
      const response = await fetchPaymentMethod(shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const getAddresses = async (): Promise<AddressesData | null> => {
    try {
      setIsLoading(true)
      const response = await fetchAddresses(shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const saveShippingAddress = async (addressInformation: ShippingAddressForm): Promise<ShippingAddressForm | null> => {
    try {
      setIsLoading(true)
      const response = await storeShippingAddress(addressInformation, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const saveTemporaryShippingAddress = async (addressInformation: ShippingAddressForm): Promise<ShippingAddressForm | null> => {
    try {
      setIsLoading(true)
      const response = await storeTemporaryShippingAddress(addressInformation, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const getShippingMethods = async (): Promise<ShippingMethodResponseModel | null> => {
    try {
      setIsLoading(true)
      const response = await fetchShippingMethods(shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const setShippingMethod = async (shippingMethodId: string): Promise<PaymentMethodResponseModel | null> => {
    try {
      setIsLoading(true)
      const response = await updateShippingMethod(shippingMethodId, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const getCountries = async (): Promise<CountriesModelResponse | null> => {
    try {
      setIsLoading(true)
      const response = await fetchCountries(shopFetch)
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const setDesiredShippingDate = async (day: number, month: number, year: number): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await updateDesiredShippingDate(day, month, year, shopFetch)
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const requestCartOrder = async (
    billingAddressName: string,
    shippingAddressName?: string | null,
    customerReferenceNumber?: string | null,
    customerComment?: string | null,
    avisInfo?: ShippingWithAvis | null
  ): Promise<CreateOrderResponseModel | null> => {
    try {
      setIsLoading(true)
      const usePortalToken = isShopAdmin ? true : false
      const response = await requestOrder(
        billingAddressName,
        shopFetch,
        shippingAddressName && shippingAddressName,
        customerReferenceNumber && customerReferenceNumber,
        customerComment && customerComment,
        avisInfo && avisInfo,
        usePortalToken && usePortalToken
      )
      return response.value
    } catch (error) {
      /** TODO: Pull out a general function for shop endpoints when they return error */
      const err = error as AuthShopFetchError
      err.response?.data.errors?.map(error => enqueueErrorStack(t(`shop.error.${error.code}`)))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const addRetrofitBatteryCart = async (productId: string, quantity: number, serialNumber: string): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await addRetrofitBatteryToCart(productId, quantity, serialNumber, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      const hasCartErrors = checkHasShopCartErrors(response.value)
      if (hasCartErrors) return null
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const addPromotionCodeCart = async (code: string): Promise<ShopCheckoutSideBarResponse | null> => {
    try {
      setIsLoading(true)
      const response = await addPromotionCodeToCart(code, shopFetch)
      const basketItemsQuantity = basketQuantityCounter(response.value?.lineItems)
      dispatch(updateShopBasketQuantity(basketItemsQuantity))
      return response.value
    } catch (error) {
      console.error(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getCheckoutProducts,
    changeProductCartQuantity,
    deleteProductCart,
    addProductCart,
    addSystemConfigurationCart,
    updateSystemConfigurationCart,
    loadPaymentMethod,
    getAddresses,
    saveShippingAddress,
    getShippingMethods,
    setShippingMethod,
    getCountries,
    setDesiredShippingDate,
    saveTemporaryShippingAddress,
    requestCartOrder,
    addRetrofitBatteryCart,
    addPromotionCodeCart,
    checkHasShopCartErrors,
    isLoading,
  }
}

export default useShopCart
